import React, { useState, useCallback, useEffect } from 'react'
import { Uploader } from 'rsuite'
import { faImage } from '@fortawesome/free-solid-svg-icons'
import { SecondarySmallButton } from './styles/Button.styles'
import {
  ImagePanel,
  ImagePanelHeader,
  DragZone,
  PreviewImage,
  UploadInstructions,
  UploadIcon,
  UploadText,
} from './styles/ImageUploader.styles'

// Type definitions for the component props
interface FileObject {
  blobFile: File
  name: string
  fileKey: string
  fromRemoteUrl?: boolean
  url?: string
  status?: 'uploading' | 'inited' | 'error' | 'finished'
}

// Rsuite FileType interface (simplified)
interface RsuiteFileType {
  blobFile?: File
  name?: string
  fileKey?: string | number
  url?: string
  status?: 'uploading' | 'inited' | 'error' | 'finished'
}

interface ImageUploaderProps {
  onFileSelect: (files: FileObject[]) => void
  onRemove: () => void
  previewUrl: string | null
  fileList: FileObject[]
  uploading: boolean
  headerText?: string
  showRemoveButton?: boolean
  imageHeight?: string
  remoteImageUrl?: string | null
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  onFileSelect,
  onRemove,
  previewUrl,
  fileList,
  uploading,
  headerText = 'Image',
  showRemoveButton = true,
  imageHeight = '200px',
  remoteImageUrl = null,
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false)

  // Effect to handle remote image loading
  useEffect(() => {
    const loadRemoteImage = async (): Promise<void> => {
      if (!remoteImageUrl || previewUrl || fileList.length > 0) return

      try {
        // Fetch the image from the remote URL
        const response = await fetch(remoteImageUrl)

        if (!response.ok) throw new Error('Failed to fetch image')

        const blob = await response.blob()

        // Create a File object from the blob
        const fileName = remoteImageUrl.split('/').pop() || 'image.jpg'
        const file = new File([blob], fileName, { type: blob.type })

        // Create a file object in the format expected by rsuite Uploader
        const fileObject: FileObject = {
          blobFile: file,
          name: fileName,
          fileKey: Date.now().toString(), // Generate a unique key
          fromRemoteUrl: true, // Add this flag to identify remote source
        }

        // Call the onFileSelect handler with our new file
        onFileSelect([fileObject])
      } catch (error) {
        console.error('Failed to load remote image:', error)
      }
    }

    loadRemoteImage()
  }, [remoteImageUrl, previewUrl, fileList.length, onFileSelect])
  const handleFileChange = useCallback(
    (files: RsuiteFileType[]) => {
      if (!files || files.length === 0) return

      // Convert RsuiteFileType to FileObject
      const convertedFiles: FileObject[] = files
        .filter(
          (file): file is Required<RsuiteFileType> & { blobFile: File } =>
            file.blobFile instanceof File,
        )
        .map((file) => ({
          blobFile: file.blobFile,
          name: file.name || file.blobFile.name,
          fileKey: String(file.fileKey || Date.now()),
          url: file.url,
          status: file.status,
        }))

      onFileSelect(convertedFiles)
    },
    [onFileSelect],
  )

  const handleError = useCallback((reason: any) => {
    console.debug('Upload error suppressed:', reason)
  }, [])
  return (
    <ImagePanel>
      <ImagePanelHeader>{headerText}</ImagePanelHeader>
      <Uploader
        action=""
        autoUpload={false}
        fileListVisible={true}
        accept="image/*"
        draggable
        multiple={false}
        fileList={fileList as RsuiteFileType[]}
        onChange={handleFileChange}
        onRemove={onRemove}
        disabled={uploading}
        listType="picture-text"
        removable={
          showRemoveButton && (Boolean(previewUrl) || Boolean(remoteImageUrl))
        }
        onDragEnter={() => setIsDragging(true)}
        onDragLeave={() => setIsDragging(false)}
        onDrop={() => setIsDragging(false)}
        onError={handleError}
      >
        <DragZone isDragging={isDragging}>
          {previewUrl ? (
            <PreviewImage
              src={previewUrl}
              alt="preview"
              style={{ height: imageHeight }}
            />
          ) : (
            <UploadInstructions>
              <UploadIcon icon={faImage} />
              <UploadText>Click or drag a file to upload</UploadText>
              <SecondarySmallButton type="button">
                Select File
              </SecondarySmallButton>
            </UploadInstructions>
          )}
        </DragZone>
      </Uploader>
    </ImagePanel>
  )
}

export default ImageUploader

export type { ImageUploaderProps, FileObject }
